import React, { useState, useEffect } from 'react';
import { independentPaymentLinksService } from '../../services/independent';
import { Search, Filter, Download, Mail, Copy, Edit2, XCircle, Bell, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';

const IndependentPaymentLinksEnhanced = () => {
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSecureModal, setShowSecureModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [formData, setFormData] = useState({
    client_email: '',
    client_name: '',
    client_phone: '',
    amount: '',
    payment_type: 'custom',
    description: '',
    custom_message: '',
    expires_in_hours: 168,
    send_email: true
  });

  useEffect(() => {
    loadPaymentLinks();
    
    const urlParams = new URLSearchParams(window.location.search);
    const clientParam = urlParams.get('client');
    
    if (clientParam) {
      try {
        const clientData = JSON.parse(decodeURIComponent(clientParam));
        setFormData(prev => ({
          ...prev,
          client_email: clientData.email || '',
          client_name: clientData.name || '',
          client_phone: clientData.phone || ''
        }));
        setShowCreateModal(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Error parsing client data:', error);
      }
    }
  }, []);

  const loadPaymentLinks = async () => {
    try {
      setLoading(true);
      const data = await independentPaymentLinksService.getAll();
      setPaymentLinks(data);
    } catch (error) {
      console.error('Error loading payment links:', error);
      alert('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_email: '',
      client_name: '',
      client_phone: '',
      amount: '',
      payment_type: 'custom',
      description: '',
      custom_message: '',
      expires_in_hours: 168,
      send_email: true
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.client_email || !formData.client_name || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount),
        expires_in_hours: parseInt(formData.expires_in_hours)
      };
      
      await independentPaymentLinksService.create(paymentData);
      await loadPaymentLinks();
      setShowCreateModal(false);
      resetForm();
      alert('Payment link created successfully!');
    } catch (error) {
      console.error('Error creating payment link:', error);
      alert('Failed to create payment link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      client_email: link.client_email,
      client_name: link.client_name,
      client_phone: link.client_phone || '',
      amount: link.amount,
      payment_type: link.payment_type,
      description: link.description || '',
      custom_message: link.custom_message || '',
      expires_in_hours: 168,
      send_email: false
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      await independentPaymentLinksService.update(editingLink.id, formData);
      await loadPaymentLinks();
      setShowEditModal(false);
      setEditingLink(null);
      resetForm();
      alert('Payment link updated successfully!');
    } catch (error) {
      console.error('Error updating payment link:', error);
      alert('Failed to update payment link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this payment link?')) return;
    
    try {
      await independentPaymentLinksService.cancel(id);
      await loadPaymentLinks();
      alert('Payment link cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      alert('Failed to cancel payment link');
    }
  };

  const handleResend = async (id) => {
    if (!window.confirm('Resend this payment link?')) return;
    
    try {
      await independentPaymentLinksService.resend(id);
      alert('Payment link resent successfully!');
    } catch (error) {
      console.error('Error resending payment link:', error);
      alert('Failed to resend payment link');
    }
  };

  const handleSendReminder = async (id) => {
    try {
      await independentPaymentLinksService.sendReminder(id);
      alert('Payment reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder');
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/payment-link/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('Payment link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  const handleViewPaymentDetails = async (paymentLinkId) => {
    try {
      const paymentLink = paymentLinks.find(link => link.id === paymentLinkId);
      if (!paymentLink || !paymentLink.payment_id) {
        alert('Payment details not available for this link');
        return;
      }

      const mockQuote = {
        payment_ids: [paymentLink.payment_id],
        payment_count: 1,
        quote_title: paymentLink.description || 'Payment Link Transaction',
        client_name: paymentLink.client_name,
        amount: paymentLink.amount,
        paymentLinkId: paymentLinkId,
        paymentLinkStatus: paymentLink.status
      };
      
      setSelectedPayment(mockQuote);
      setShowSecureModal(true);
    } catch (error) {
      console.error('Error preparing payment details:', error);
      alert('Failed to load payment details');
    }
  };

  const closeSecureModal = () => {
    setShowSecureModal(false);
    setSelectedPayment(null);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLinks(filteredLinks.map(link => link.id));
    } else {
      setSelectedLinks([]);
    }
  };

  const handleSelectLink = (linkId, checked) => {
    if (checked) {
      setSelectedLinks([...selectedLinks, linkId]);
    } else {
      setSelectedLinks(selectedLinks.filter(id => id !== linkId));
    }
  };

  const handleBulkCancel = async () => {
    if (!window.confirm(`Cancel ${selectedLinks.length} payment links?`)) return;
    
    try {
      await Promise.all(selectedLinks.map(id => independentPaymentLinksService.cancel(id)));
      await loadPaymentLinks();
      setSelectedLinks([]);
      alert(`${selectedLinks.length} payment links cancelled!`);
    } catch (error) {
      console.error('Error cancelling payment links:', error);
      alert('Failed to cancel some payment links');
    }
  };

  const handleBulkResend = async () => {
    if (!window.confirm(`Resend ${selectedLinks.length} payment links?`)) return;
    
    try {
      await Promise.all(selectedLinks.map(id => independentPaymentLinksService.resend(id)));
      setSelectedLinks([]);
      alert(`${selectedLinks.length} payment links resent!`);
    } catch (error) {
      console.error('Error resending payment links:', error);
      alert('Failed to resend some payment links');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const filteredLinks = paymentLinks
    .filter(link => {
      if (statusFilter !== 'all' && link.status !== statusFilter) return false;
      if (paymentTypeFilter !== 'all' && link.payment_type !== paymentTypeFilter) return false;
      if (searchTerm && !(
        link.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.client_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )) return false;
      return true;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (sortBy === 'created_at' || sortBy === 'expires_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  const stats = {
    total: filteredLinks.length,
    pending: filteredLinks.filter(l => l.status === 'pending').length,
    completed: filteredLinks.filter(l => l.status === 'paid').length,
    totalAmount: filteredLinks.filter(l => l.status === 'paid').reduce((sum, l) => sum + parseFloat(l.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Links</h1>
          <p className="text-gray-600 mt-1">Create and manage secure payment collection links</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Mail size={20} />
          Create Payment Link
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Links</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Collected</p>
              <p className="text-2xl font-bold text-blue-600">${stats.totalAmount.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 text-xl font-bold">$</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <select
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="final">Final Payment</option>
              <option value="partial">Partial Payment</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>

        {selectedLinks.length > 0 && (
          <div className="mt-4 flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
            <span className="text-sm text-gray-700">{selectedLinks.length} selected</span>
            <button
              onClick={handleBulkResend}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Resend Selected
            </button>
            <button
              onClick={handleBulkCancel}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Cancel Selected
            </button>
          </div>
        )}
      </div>

      {filteredLinks.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Mail className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No payment links found</h3>
          <p className="text-gray-600 mb-4">Create your first payment link to get started</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Payment Link
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden mt-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLinks.length === filteredLinks.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded"
                    />
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_at')}
                  >
                    Created {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('expires_at')}
                  >
                    Expires {sortBy === 'expires_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLinks.map((link) => (
                  <tr key={link.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLinks.includes(link.id)}
                        onChange={(e) => handleSelectLink(link.id, e.target.checked)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(link.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{link.client_name}</div>
                      <div className="text-sm text-gray-500">{link.client_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      ${parseFloat(link.amount).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                        {link.payment_type?.replace('_', ' ') || 'Custom'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        link.status === 'paid' ? 'bg-green-100 text-green-800' :
                        link.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        link.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {link.status.charAt(0).toUpperCase() + link.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={isExpired(link.expires_at) ? 'text-red-600 font-medium' : 'text-gray-600'}>
                        {new Date(link.expires_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        {link.status === 'pending' && !isExpired(link.expires_at) && (
                          <>
                            <button
                              onClick={() => copyLink(link.session_token)}
                              className="p-1 text-gray-600 hover:text-blue-600"
                              title="Copy Link"
                            >
                              <Copy size={16} />
                            </button>
                            <button
                              onClick={() => handleResend(link.id)}
                              className="p-1 text-gray-600 hover:text-green-600"
                              title="Resend"
                            >
                              <Mail size={16} />
                            </button>
                            <button
                              onClick={() => handleSendReminder(link.id)}
                              className="p-1 text-gray-600 hover:text-yellow-600"
                              title="Send Reminder"
                            >
                              <Bell size={16} />
                            </button>
                            <button
                              onClick={() => handleEdit(link)}
                              className="p-1 text-gray-600 hover:text-blue-600"
                              title="Edit"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleCancel(link.id)}
                              className="p-1 text-gray-600 hover:text-red-600"
                              title="Cancel"
                            >
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        {link.status === 'paid' && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-green-600 flex items-center">
                              <CheckCircle size={16} className="mr-1" />
                              Paid
                            </span>
                            <button
                              onClick={() => handleViewPaymentDetails(link.id)}
                              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              title="View secure payment details"
                            >
                              💳 View Payment Details
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {showCreateModal ? 'Create Payment Link' : 'Edit Payment Link'}
              </h2>
              <form onSubmit={showCreateModal ? handleCreate : handleUpdate}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount *
                    </label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Type
                    </label>
                    <select
                      value={formData.payment_type}
                      onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="deposit">Deposit</option>
                      <option value="final">Final Payment</option>
                      <option value="partial">Partial Payment</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expires In (hours)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.expires_in_hours}
                      onChange={(e) => setFormData({ ...formData, expires_in_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Message
                    </label>
                    <textarea
                      value={formData.custom_message}
                      onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.send_email}
                        onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Send email notification to client</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setEditingLink(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {submitting ? 'Saving...' : showCreateModal ? 'Create Link' : 'Update Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showSecureModal && selectedPayment && (
        <SecurePaymentDetailsModal
          isOpen={showSecureModal}
          quote={selectedPayment}
          onClose={closeSecureModal}
          onPaymentDeleted={loadPaymentLinks}
          isPaymentLink={true}
          paymentLinkId={selectedPayment.paymentLinkId}
        />
      )}
    </div>
  );
};

export default IndependentPaymentLinksEnhanced;
